import { ElementCategoryID } from '../models/element-categories.model';
import {
  ElementPropertyName,
  IElementProperty,
  IFactoryProperty,
} from '../models/element-property.model';
import { ElementKind } from '../models/project.model';
import { SbefCode, SbefGroupCode } from '../construction/sbef.model';
import { getEntries, omitUndefined } from './object.helpers';
import { createElement } from './element-factory.helpers';
import { getElementPropertiesFromCategory } from './element-category.helpers';
import {
  isSelectPropertyOptionValue,
  isElementSelectProperty,
} from './element-property.helpers';
import { getSbefScopeGroupKeyForCode } from './sbef-grouping.helpers';
import { tryCatch } from './utils.helpers';
import { guards } from './typeguards.helpers';

/**
 * Property values that may be seeded on an element at creation time, so the element is born
 * with them instead of being created and then patched by a second write (DEV-3507).
 *
 * A whitelist, not `Record<ElementPropertyName, unknown>`, because only some properties survive
 * creation: enrichment re-derives category rows and carries over just the `count`, matched by
 * name. To make another property settable, add its name here with the value type it accepts, and
 * check that:
 *
 * - **The category template defines it for a bare element.** Conditional rows (filtered in by
 *   another property's value, e.g. rows that appear once `material` is set) do not exist yet at
 *   creation, so they come back as `unknown-property` — reported, never seeded.
 * - **Its value can be checked up front.** Select values are validated against the property
 *   options here; an unchecked bad value is stored silently and only fails later, at the
 *   user-input boundary.
 * - **It is not `quantity`.** Quantity is a separate field with its own expression handling, not
 *   an entry in `properties`.
 *
 * Template-driver properties (`material` and friends) are settable in principle, but they reshape
 * which rows the template produces — expect an enrich cascade and test that case specifically.
 */
export type SettableElementPropertyValues = {
  [ElementPropertyName.SbefCode]?: SbefCode;
};

/** Why a requested value in {@link SettableElementPropertyValues} could not be applied. */
export type InvalidElementPropertyValueReason =
  /** The category template does not define this property (wrong category, or conditional row). */
  | 'unknown-property'
  /** The property exists but the value is not one of its options. */
  | 'invalid-count'
  /** Resolving the category template threw. */
  | 'template-error';

/** A requested value that was ignored, for reporting by the caller. */
export type InvalidElementPropertyValue = {
  name: ElementPropertyName;
  count: unknown;
  reason: InvalidElementPropertyValueReason;
};

export type ResolvedElementPropertyValues = {
  /** Factory rows to merge into the created element's `properties`. */
  properties: IFactoryProperty[];
  /** Values that were dropped. Never throws — callers report these instead. */
  invalid: InvalidElementPropertyValue[];
};

/** Fresh arrays per call — a shared constant would let one caller's mutation leak into the next. */
const emptyResult = (): ResolvedElementPropertyValues => ({
  properties: [],
  invalid: [],
});

/**
 * The category template rows a freshly created element of `categoryId` would get.
 * Conditional rows that depend on other property values are absent by design — a bare element
 * has nothing for them to key off — which is why those are not settable at creation time.
 */
const getTemplateProperties = (
  categoryId: ElementCategoryID,
): IElementProperty[] =>
  getElementPropertiesFromCategory(
    createElement({ kind: ElementKind.Element, category_id: categoryId }),
    categoryId,
  );

/**
 * Turn requested {@link SettableElementPropertyValues} into factory property rows for a new
 * element of `categoryId`.
 *
 * Values that the category does not support are ignored rather than thrown, so a bad caller can
 * never break element creation; they come back in `invalid` for the caller to report.
 */
export const resolveElementPropertyValues = (
  categoryId: ElementCategoryID,
  values: SettableElementPropertyValues = {},
): ResolvedElementPropertyValues => {
  // Widened to `unknown` on purpose: the value is only trusted once matched against the
  // category template below, so the boundary does not need the per-name value type.
  const requested = getEntries(omitUndefined(values)).filter(
    guards.is.defined,
  ) as [ElementPropertyName, unknown][];
  if (!requested.length) return emptyResult();

  const template = tryCatch(() => getTemplateProperties(categoryId));
  if (!template.ok) {
    return {
      properties: [],
      invalid: requested.map(([name, count]) => ({
        name,
        count,
        reason: 'template-error',
      })),
    };
  }

  const properties: IFactoryProperty[] = [];
  const invalid: InvalidElementPropertyValue[] = [];

  for (const [name, count] of requested) {
    const templateProperty = template.value.find((p) => p.name === name);

    if (!templateProperty) {
      invalid.push({ name, count, reason: 'unknown-property' });
      continue;
    }

    if (
      isElementSelectProperty(templateProperty) &&
      !isSelectPropertyOptionValue(templateProperty, count)
    ) {
      invalid.push({ name, count, reason: 'invalid-count' });
      continue;
    }

    // Seeded from the template row, so the definition (type, options, source) is the category's.
    // Enrichment replaces the row with a freshly derived one but reuses the count by name.
    properties.push({ ...templateProperty, count } as IFactoryProperty);
  }

  return { properties, invalid };
};

/**
 * Whether a bare element of `categoryId` defines `name`.
 * A thrown template counts as defined so {@link resolveElementPropertyValues} still
 * reports `template-error` instead of this check hiding it.
 */
const categoryTemplateDefinesProperty = (
  categoryId: ElementCategoryID,
  name: ElementPropertyName,
): boolean => {
  const template = tryCatch(() => getTemplateProperties(categoryId));
  if (!template.ok) return true;
  return template.value.some((property) => property.name === name);
};

/**
 * Property values that place a new element in `sbefCode`'s SBEF group.
 *
 * Undefined when there is no code, or when a bare element of `categoryId` has no
 * {@link ElementPropertyName.SbefCode} row. Maintenance omits that row on purpose;
 * seeding the code is dropped as `unknown-property` and reported (DEV-3962).
 */
export const propertyValuesForNewElementSbefCode = (
  categoryId: ElementCategoryID,
  sbefCode: SbefCode | undefined,
): SettableElementPropertyValues | undefined => {
  if (!sbefCode) return undefined;
  // A missing row cannot store the code. Asking for it only produces a warning.
  if (
    !categoryTemplateDefinesProperty(categoryId, ElementPropertyName.SbefCode)
  ) {
    return undefined;
  }
  return { [ElementPropertyName.SbefCode]: sbefCode };
};

/** Seed and list group for a new element offered an SBEF code. */
export type NewElementSbefPlacement = {
  /**
   * Values to pass to add. Undefined when there is no code, or when a bare
   * element of the category has no {@link ElementPropertyName.SbefCode} row.
   */
  propertyValues: SettableElementPropertyValues | undefined;
  /**
   * Element-list group to expand. The stored code's scope group, or Site
   * operations when {@link NewElementSbefPlacement.propertyValues} is undefined.
   * Ungrouped content is listed under Site operations.
   */
  groupKey: SbefGroupCode | string;
};

/**
 * Where a new element offered `sbefCode` is stored, and which list group to expand.
 *
 * The element-list header expands {@link NewElementSbefPlacement.groupKey} after add.
 * An SBEF group row passes {@link NewElementSbefPlacement.propertyValues} and omits
 * them when undefined, so a category with no `sbef_code` row stays ungrouped.
 */
export const placementForNewElementSbefCode = (
  categoryId: ElementCategoryID,
  sbefCode: SbefCode | undefined,
): NewElementSbefPlacement => {
  const propertyValues = propertyValuesForNewElementSbefCode(
    categoryId,
    sbefCode,
  );
  // No stored code, and A5 codes, fold into Site operations. A standard code maps to its group.
  return {
    propertyValues,
    groupKey: getSbefScopeGroupKeyForCode(
      propertyValues?.[ElementPropertyName.SbefCode],
    ),
  };
};
